import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import { TextButton } from '../Button';
import { supabase } from '../../lib/supabase';
import languagesData from '../../data/languages.json';
import Dropdown from '../Dropdown/Dropdown';
import './ExportTranslationsDialog.css';

interface ExportTranslationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectLanguages: string[];
  placeholderFormat?: string;
  isLoading?: boolean;
}

const ExportTranslationsDialog: React.FC<ExportTranslationsDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  projectLanguages,
  placeholderFormat = 'web',
  isLoading = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(projectLanguages);
  const [exportOptions, setExportOptions] = useState({
    pretty: true,
    flattenStructure: false,
    format: 'json' as 'json' | 'strings' | 'xml',
    targetPlaceholderFormat: placeholderFormat
  });
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const [dialogMounted, setDialogMounted] = useState(false);
  
  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setDialogMounted(true);
      setSelectedLanguages(projectLanguages);
      setExportProgress(null);
      setError(null);
      setIsProcessing(false);
      setDownloadStatus(null);
      setExportOptions(prev => ({
        ...prev,
        targetPlaceholderFormat: placeholderFormat
      }));
    } else {
      // Delay unmounting to allow for exit animations
      const timer = setTimeout(() => {
        setDialogMounted(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, projectLanguages, placeholderFormat]);

  const handleLanguageToggle = (languageCode: string) => {
    setSelectedLanguages(prev => {
      if (prev.includes(languageCode)) {
        return prev.filter(code => code !== languageCode);
      } else {
        return [...prev, languageCode];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLanguages.length === projectLanguages.length) {
      setSelectedLanguages([]);
    } else {
      setSelectedLanguages(projectLanguages);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for "Select none" UI
  const handleSelectNone = () => {
    setSelectedLanguages([]);
  };

  const handleOptionChange = (option: keyof typeof exportOptions) => {
    setExportOptions({
      ...exportOptions,
      [option]: !exportOptions[option],
    });
  };

  // Helper function to download a file using the FileSaver approach
  const saveAs = (blob: Blob, filename: string): void => {
    try {
      // Create object URL
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = filename;
      
      // Add to document, click, and remove
      document.body.appendChild(link);
      
      // Use setTimeout to ensure the browser processes the DOM changes
      setTimeout(() => {
        link.click();
        
        // Remove link after click
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 200);
      }, 0);
    } catch (err) {
      console.error('Error saving file:', err);
      
      // Fallback method
      try {
        // Try to open in new window as fallback
        const reader = new FileReader();
        reader.onload = function() {
          const dataUrl = reader.result as string;
          const win = window.open();
          if (win) {
            win.document.write(`
              <html>
                <head>
                  <title>${filename}</title>
                </head>
                <body>
                  <h3>Right-click and choose "Save As" to download the file</h3>
                  <pre>${dataUrl}</pre>
                </body>
              </html>
            `);
          }
        };
        reader.readAsText(blob);
      } catch (fallbackErr) {
        console.error('Fallback download method failed:', fallbackErr);
      }
    }
  };

  const handleExport = async () => {
    if (selectedLanguages.length === 0) {
      setError('Please select at least one language to export');
      return;
    }

    if (!projectId) {
      setError('No project selected');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setExportProgress('0');
      setDownloadStatus('Preparing export...');

      // Simulate progress updates
      const progressSteps = [
        { step: 'Gathering translations', duration: 2000 },
        { step: 'Processing files', duration: 2000 },
        { step: 'Creating ZIP file', duration: 1000 },
        { step: 'Downloading', duration: 1000 }
      ];

      // Get the current session
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      // Start the progress simulation
      let currentProgress = 0;
      for (const { step, duration } of progressSteps) {
        setDownloadStatus(step);
        setExportProgress(currentProgress.toString());
        await new Promise(resolve => setTimeout(resolve, duration));
        currentProgress += 25;
      }

      // Use the production URL for the deployed version
      const functionUrl = process.env.REACT_APP_SUPABASE_EXPORT_TRANSLATIONS_URL;
      if (!functionUrl) {
        throw new Error('Export Translations function URL must be set in environment variables.');
      }

      // Create fetch request
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          projectId,
          options: {
            languagesToExport: selectedLanguages,
            formatOptions: {
              pretty: exportOptions.pretty,
              flattenStructure: exportOptions.flattenStructure,
              format: exportOptions.format,
              targetPlaceholderFormat: exportOptions.targetPlaceholderFormat
            }
          }
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to export translations';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `Export failed: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Get the response as a blob and save it
      const content = await response.blob();
      saveAs(content, `translations-${projectId}.zip`);

      // Set final progress
      setExportProgress('100');
      setDownloadStatus('Complete');

      // Close the dialog after a delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export translations');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatOptions = [
    {
      id: 'formats',
      items: [
        { id: 'json', label: 'JSON', value: 'json' },
        { id: 'strings', label: 'iOS Strings', value: 'strings' },
        { id: 'xml', label: 'XML', value: 'xml' }
      ]
    }
  ];

  const placeholderFormatOptions = [
    {
      id: 'formats',
      items: [
        { id: 'web', label: 'Web {name}', value: 'web' },
        { id: 'unified', label: 'Unified [name]', value: 'unified' },
        { id: 'ios', label: 'iOS %@', value: 'ios' },
        { id: 'android', label: 'Android %1$s', value: 'android' },
        { id: 'lokalise', label: 'Lokalise [%name$s]', value: 'lokalise' }
      ]
    }
  ];

  // Ensure the dialog is rendered even when not open to allow for animations
  return dialogMounted ? (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        if (!isProcessing) {
          onClose();
        }
      }}
      title="Export"
      className="export-translations-dialog"
      actions={
        <>
          <TextButton 
            onClick={onClose} 
            disabled={isProcessing || isLoading}
            size="small"
            variant="secondary"
          >
            Cancel
          </TextButton>
          <TextButton
            onClick={handleExport}
            disabled={selectedLanguages.length === 0 || isProcessing || isLoading}
            variant="primary"
            size="small"
          >
            {isProcessing ? 'Exporting...' : 'Export'}
          </TextButton>
        </>
      }
    >
      <div className="export-translations-content">
        <div className={`language-selection-container ${isProcessing ? 'processing' : ''}`}>
          <div className="language-selection-header">
            <h3 className="section-title">Select languages</h3>
          </div>
          
          {!isProcessing && (
            <div className="language-list">
              {projectLanguages.length === 0 ? (
                <p className="empty-state">No languages available in this project</p>
              ) : (
                <>
                  <label className="language-item">
                    <input
                      type="checkbox"
                      checked={selectedLanguages.length === projectLanguages.length}
                      onChange={handleSelectAll}
                    />
                    <div className="language-info">
                      <span className="language-name">All Languages</span>
                    </div>
                  </label>
                  {selectedLanguages.length !== projectLanguages.length && projectLanguages.map(langCode => {
                    const language = languagesData.languages.find(l => l.code === langCode);
                    const isSelected = selectedLanguages.includes(langCode);
                    return (
                      <label key={langCode} className="language-item">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleLanguageToggle(langCode)}
                        />
                        <div className="language-info">
                          <span className="language-name">{language?.name || langCode}</span>
                          <span className="language-code">{langCode}</span>
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
        
        {!isProcessing && (
          <div className="export-options">
            <div className="option-label format-selector">
              <div className="option-text">
                <span className="option-title">File Format</span>
              </div>
              <Dropdown
                items={formatOptions}
                selectedId={exportOptions.format}
                onSelect={(value) => setExportOptions(prev => ({ ...prev, format: value as 'json' | 'strings' | 'xml' }))}
                disabled={isProcessing}
                position="right"
                hasSections={true}
              />
            </div>

            <div className="option-label format-selector">
              <div className="option-text">
                <span className="option-title">Placeholder Format</span>
              </div>
              <Dropdown
                items={placeholderFormatOptions}
                selectedId={exportOptions.targetPlaceholderFormat}
                onSelect={(value) => setExportOptions(prev => ({ ...prev, targetPlaceholderFormat: value }))}
                disabled={isProcessing}
                position="right"
                hasSections={true}
              />
            </div>

            <label className={`option-label ${isProcessing ? 'disabled' : ''}`}>
              
              <div className="option-text">
                <span className="option-title">Export pluralized strings as separate keys</span>
              </div>
              <input
                type="checkbox"
                checked={exportOptions.flattenStructure}
                onChange={() => handleOptionChange('flattenStructure')}
                disabled={isProcessing}
              />
            </label>
          </div>
        )}

        {isProcessing && (
          <div className="export-progress-container">
            <div className="progress-status">
              <span className="progress-label">
                {downloadStatus || "Exporting translations..."}
              </span>
              <span className="progress-percentage">{exportProgress}%</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ width: `${exportProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {error && <div className="export-error">{error}</div>}
      </div>
    </Dialog>
  ) : null;
};

export default ExportTranslationsDialog; 